// Betty's "setting to change" lines are stored as `field: old -> new` (the format the apply step
// reads). The practice sees them in words: "Earliest text: 10:00 AM → 11:00 AM".

type Kind = 'minutes' | 'time' | 'onOff' | 'text';

const FIELDS: Record<string, { label: string; kind: Kind }> = {
  delayminutes: { label: 'Wait after the visit', kind: 'minutes' },
  windowstartlocal: { label: 'Earliest text', kind: 'time' },
  windowendlocal: { label: 'Latest text', kind: 'time' },
  sendonsundays: { label: 'Send on Sundays', kind: 'onOff' },
  reminderenabled: { label: 'One reminder if there’s no reply', kind: 'onOff' },
  eodreminderenabled: { label: 'End-of-day reminder email', kind: 'onOff' },
  eodreminderlocal: { label: 'End-of-day reminder time', kind: 'time' },
  texttimezone: { label: 'Time zone for texts', kind: 'text' },
  practicetimezone: { label: 'Time zone for texts', kind: 'text' },
};

const LINE = /^\s*[-*•]?\s*`?([\w.]+)`?\s*:\s*(.+?)\s*(?:->|→)\s*(.+?)\s*$/;

/** "privateFeedbackRecipients" → "Private feedback recipients". */
function words(field: string): string {
  const spaced = field
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function value(raw: string, kind: Kind): string {
  const v = raw.trim().replace(/^["'`]|["'`]$/g, '');
  if (v === '[]' || v === '' || v === 'null') return 'none';
  if (kind === 'onOff') {
    if (/^(true|on|yes)$/i.test(v)) return 'On';
    if (/^(false|off|no)$/i.test(v)) return 'Off';
  }
  if (kind === 'minutes') {
    const n = Number(v.replace(/\s*(min|minutes)$/i, ''));
    if (Number.isFinite(n) && n > 0) {
      if (n % 60 === 0) return `${n / 60} hour${n === 60 ? '' : 's'}`;
      return `${n} minutes`;
    }
  }
  if (kind === 'time') {
    const m = /^(\d{1,2}):(\d{2})$/.exec(v);
    if (m) {
      const h = Number(m[1]);
      return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
    }
  }
  return v;
}

/** Whether the text names at least one setting approving can actually change. */
export function namesApplicableSetting(text: string): boolean {
  return text.split('\n').some((line) => {
    const m = LINE.exec(line);
    return (
      !!m &&
      !!FIELDS[
        m[1]!
          .split('.')
          .pop()!
          .replace(/[^a-z]/gi, '')
          .toLowerCase()
      ]
    );
  });
}

/** One line per change, in words; a line that is not a change is kept as written. */
export function settingsChangeText(text: string): string {
  return text
    .split('\n')
    .map((line) => {
      const m = LINE.exec(line);
      if (!m) return line;
      const key = m[1]!
        .split('.')
        .pop()!
        .replace(/[^a-z]/gi, '')
        .toLowerCase();
      const known = FIELDS[key];
      const kind = known?.kind ?? 'text';
      const label = known?.label ?? words(m[1]!.split('.').pop()!);
      return `${label}: ${value(m[2]!, kind)} → ${value(m[3]!, kind)}`;
    })
    .join('\n');
}

/** Section keys that hold `field: old -> new` lines. */
// `change` is also a prose section for some skills: lines that are not changes stay as written.
export const SETTINGS_CHANGE_KEYS: ReadonlySet<string> = new Set(['settingsChange', 'change']);
